import { createContext, ReactNode, useContext, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { ComparisonStoreType, createComparisonStore } from '../store';

const ComparisonStoreContext = createContext<ReturnType<typeof createComparisonStore> | null>(null);

type ComparisonStoreProviderProps = {
    children: ReactNode;
};

export function ComparisonStoreProvider({ children }: ComparisonStoreProviderProps) {
    const [store] = useState(() => createComparisonStore());

    return (
        <ComparisonStoreContext.Provider value={store}>{children}</ComparisonStoreContext.Provider>
    );
}

export function useComparisonStore<T>(selector: (state: ComparisonStoreType) => T): T {
    const store = useContext(ComparisonStoreContext);
    if (!store) {
        throw new Error('useComparisonStore must be used within a ComparisonStoreProvider');
    }
    return useStore(store, useShallow(selector));
}

// ─── Selector hooks ───────────────────────────────────────────────────────────

export const useComparisonCompanies = () => useComparisonStore((s) => s.companies);
export const useComparisonSchemaTypes = () => useComparisonStore((s) => s.selectedSchemaTypes);
export const useComparisonSchemaData = () => useComparisonStore((s) => s.schemaData);
export const useComparisonFilterCfg = () => useComparisonStore((s) => s.filterCfg);
export const useComparisonTableConfig = () => useComparisonStore((s) => s.tableConfig);

export const useComparisonCompanyActions = () => useComparisonStore((s) => s.companyActions);
export const useComparisonSchemaActions = () => useComparisonStore((s) => s.schemaActions);
export const useComparisonConfigActions = () => useComparisonStore((s) => s.configActions);
